import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {AsyncPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators,} from '@angular/forms';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatCard, MatCardActions, MatCardContent} from '@angular/material/card';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from '@angular/material/divider';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatList, MatListItem, MatListItemIcon, MatListItemLine} from '@angular/material/list';
import {MatTree, MatTreeNode, MatTreeNodeDef, MatTreeNodePadding, MatTreeNodeToggle,} from '@angular/material/tree';
import {Client, ClientService} from '@app/admin/client.service';
import {IdenticonPipe} from '@app/shared/identicon.pipe';
import {StringDialogComponent} from '@app/shared/string-dialog/string-dialog.component';
import {User} from '@app/shared/user.service';
import {AddMembersDialogComponent} from '@app/stream/add-members-dialog/add-members-dialog.component';
import {Stream} from '@app/stream/models';
import {StreamService} from '@app/stream/stream.service';
import {StreamTree} from '@app/stream/streamtree';
import {Observable} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';

interface StreamNode {
    expandable: boolean;
    name: string;
    level: number;
    parent: string;
    stream: Stream;
}

@Component({
    selector: 'frog-stream-admin',
    templateUrl: './stream-admin.component.html',
    styleUrls: ['./stream-admin.component.scss'],
    imports: [
        MatButton,
        MatIcon,
        MatTree,
        MatTreeNodeDef,
        MatTreeNode,
        MatTreeNodePadding,
        MatIconButton,
        MatTreeNodeToggle,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatCheckbox,
        MatDivider,
        MatList,
        MatListItem,
        MatListItemIcon,
        MatListItemLine,
        MatCardActions,
        AsyncPipe,
        IdenticonPipe,
    ],
})
export class StreamAdminComponent implements OnInit {
    public streams$: Observable<Stream[]>;
    public client$: Observable<Client>;
    public form: UntypedFormGroup;
    public activeStream: Stream;
    public streamToDelete: Stream;
    public streamTree: StreamTree;
    public members: User[];

    constructor(
        private streamService: StreamService,
        private clientService: ClientService,
        private matDialog: MatDialog,
        private formBuilder: UntypedFormBuilder,
    ) {
        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            description: '',
            parent: null,
            inclusive: false,
            include_slack: false,
            read_only: false,
            private: false,
        });
        this.activeStream = null;
        this.streamToDelete = null;
        this.streamTree = new StreamTree();
        this.members = [];
    }

    private _transformer(node: Stream, level: number) {
        return {
            expandable: !!node.children && node.children.length > 0,
            name: node.name,
            level: level,
            parent: node.parent,
            stream: node,
        };
    }

    hasChild(_: number, node: StreamNode) {
        return node.expandable;
    }

    isPrivate(stream: Stream): boolean {
        if (stream.parent) {
            const parent = this.streamService.getStream(stream.parent);
            if (parent) {
                return parent.private;
            }
            return false;
        }

        return stream.private;
    }

    ngOnInit(): void {
        this.streams$ = this.streamService.streams.pipe(
            map((streams) => streams.filter((stream) => stream.parent === null)),
            tap((streams) => {
                this.streamTree.dataSource.data = streams;
                this.streamTree.treeControl.expandAll();
                if (this.activeStream !== null) {
                    const activeStream = streams.filter(
                        (stream) => stream.id === this.activeStream.id,
                    );
                    if (activeStream.length > 0) {
                        this.selectStream(activeStream[0]);
                    }
                }
            }),
        );

        this.streamService.initRequests();
        this.client$ = this.clientService.client;
    }

    drop(event: CdkDragDrop<string[]>) {
        // moveItemInArray(this.movies, event.previousIndex, event.currentIndex);
    }

    selectStream(stream: Stream) {
        this.activeStream = stream;
        this.streamToDelete = null;
        this.form.setValue({
            name: stream.name,
            description: stream.description,
            parent: stream.parent,
            inclusive: stream.inclusive,
            include_slack: stream.include_slack,
            read_only: stream.read_only,
            private: stream.private,
        });
        this.members = stream.members;
    }

    saveStream() {
        this.streamService.updateStream(this.activeStream.id, this.form.value);
        this.form.markAsPristine();
    }

    addStream(parent?: Stream) {
        const data = {
            title: 'Stream Name',
            message: 'Enter the name of the new stream',
        };
        const dialog = this.matDialog.open(StringDialogComponent, { data });
        dialog
            .afterClosed()
            .pipe(filter((result) => result !== null))
            .subscribe((name) => {
                if (parent) {
                    this.streamService.createStream(name, parent.id);
                } else {
                    this.streamService.createStream(name);
                }
            });
    }

    removeStream(merge: boolean) {
        this.streamService.removeStream(this.streamToDelete.id, merge);
        this.streamToDelete = null;
    }

    initiateDelete() {
        this.streamToDelete = this.activeStream;
    }

    addMembers() {
        const dialog = this.matDialog.open(AddMembersDialogComponent, {
            data: this.activeStream,
            width: '80vw',
        });
        dialog
            .afterClosed()
            .pipe(
                filter((users) => Boolean(users)),
                map((users) => this.streamService.addMembers(this.activeStream.id, users)),
            )
            .subscribe((users) => {});
    }

    removeMember(member: User) {
        this.streamService.removeMember(this.activeStream.id, member);
    }
}
